// client/src/pages/VerifyCertificate.js
import React, { useState } from "react";
import { api } from "../api";
import { 
  CheckCircle, 
  XCircle, 
  ShieldCheck, 
  Search, 
  Award, 
  User, 
  Calendar,
  Hash,
  ArrowRight,
  Loader2
} from "lucide-react";
import Logo from "../assets/logo.png"; // Ensure path matches

function VerifyCertificate() {
  const [certId, setCertId] = useState("");
  const [candidateName, setCandidateName] = useState("");

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | success | not_found | error
  const [result, setResult] = useState(null);   // API success payload
  const [message, setMessage] = useState("");   // error / info message

  // --- LOGIC SECTION (PRESERVED 100%) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("idle");
    setResult(null);
    setMessage("");

    const trimmedCert = certId.trim();
    const trimmedName = candidateName.trim();

    if (!trimmedCert && !trimmedName) {
      setStatus("error");
      setMessage("Please enter a certificate ID or candidate name.");
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (trimmedCert) params.append("certId", trimmedCert);
      if (trimmedName) params.append("name", trimmedName);

      const res = await api.get(`/api/certificates/verify?${params.toString()}`);

      // If we got here without throwing, it's a 2xx
      setResult(res.data || null);
      setStatus("success");
      setMessage("");
    } catch (err) {
      const statusCode = err.response?.status;

      if (statusCode === 404) {
        setStatus("not_found");
        setResult(null);
        setMessage(
          err.response?.data?.message ||
            "No matching certificate record was found."
        );
      } else {
        console.error("Verify error:", err.response?.data || err.message);
        setStatus("error");
        setResult(null);
        setMessage(
          err.response?.data?.message ||
            "Verification failed. Please try again later."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // --- UI RENDER SECTION (RE-DESIGNED) ---

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-900">
      
      {/* Navbar (Minimal) */}
      <header className="h-20 px-8 flex items-center justify-between max-w-7xl mx-auto w-full">
         <div className="flex items-center gap-3">
            <img src={Logo} alt="Orivon" className="h-8 w-auto" />
            <div className="w-px h-6 bg-gray-300"></div>
            <span className="text-gray-500 font-medium text-sm tracking-wide uppercase">Verification Portal</span>
         </div>
         <a href="/" className="text-sm font-semibold text-gray-600 hover:text-orange-600 transition-colors">
            Back to Home
         </a>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl bg-white rounded-3xl shadow-2xl shadow-gray-200 overflow-hidden flex flex-col md:flex-row min-h-[600px] border border-gray-100">
          
          {/* Left Side: Brand & Visuals */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-12 md:w-2/5 flex flex-col relative overflow-hidden">
             {/* Decorative Elements */}
             <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 -mr-16 -mt-16"></div>
             <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 -ml-16 -mb-16"></div>
             
             <div className="relative z-10">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-8 backdrop-blur-md border border-white/20">
                   <ShieldCheck size={28} className="text-orange-400" />
                </div>
                
                <h1 className="text-3xl font-bold mb-4 leading-tight">Authenticity Matters.</h1>
                <p className="text-gray-300 text-lg leading-relaxed mb-8">
                   Verify credentials issued by Orivon Academy instantly. Ensure the validity of certifications for hiring or academic purposes.
                </p>

                <div className="mt-auto space-y-4">
                   <div className="flex items-center gap-4 text-sm text-gray-300">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 border border-green-500/30">
                         <CheckCircle size={16} />
                      </div>
                      <p>Instant Real-time Verification</p>
                   </div>
                   <div className="flex items-center gap-4 text-sm text-gray-300">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 border border-blue-500/30">
                         <ShieldCheck size={16} />
                      </div>
                      <p>Tamper-proof Records</p>
                   </div>
                </div>
             </div>
          </div>

          {/* Right Side: Form & Results */}
          <div className="p-12 md:w-3/5 bg-white flex flex-col justify-center">
             
             {/* Search Form */}
             <div className="max-w-md mx-auto w-full">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Verify a Certificate</h2>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                   <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Certificate ID</label>
                      <div className="relative group">
                         <Hash className="absolute left-3 top-3.5 text-gray-400 group-focus-within:text-orange-500 transition-colors" size={18} />
                         <input 
                           type="text" 
                           value={certId}
                           onChange={(e) => setCertId(e.target.value)}
                           className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all font-mono text-sm"
                           placeholder="e.g. ORI-2025-X89Z"
                         />
                      </div>
                   </div>
                   
                   <div className="relative flex items-center py-2">
                      <div className="flex-grow border-t border-gray-100"></div>
                      <span className="flex-shrink-0 mx-4 text-gray-400 text-xs font-medium uppercase">Or search by name</span>
                      <div className="flex-grow border-t border-gray-100"></div>
                   </div>

                   <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Candidate Name</label>
                      <div className="relative group">
                         <User className="absolute left-3 top-3.5 text-gray-400 group-focus-within:text-orange-500 transition-colors" size={18} />
                         <input 
                           type="text" 
                           value={candidateName}
                           onChange={(e) => setCandidateName(e.target.value)}
                           className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                           placeholder="e.g. John Doe"
                         />
                      </div>
                   </div>

                   <button 
                     type="submit" 
                     disabled={loading}
                     className="w-full bg-gray-900 text-white font-bold py-3.5 rounded-xl hover:bg-orange-600 transition-all shadow-lg hover:shadow-orange-500/25 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed mt-4"
                   >
                     {loading ? <Loader2 size={20} className="animate-spin"/> : <Search size={20} />}
                     {loading ? "Verifying..." : "Verify Now"}
                   </button>
                </form>

                {/* --- RESULTS SECTION --- */}
                <div className="mt-8 transition-all duration-300">
                   
                   {/* SUCCESS STATE */}
                   {status === "success" && result && (
                      <div className="bg-green-50 border border-green-100 rounded-2xl p-6 relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                         <div className="absolute top-0 right-0 p-4 opacity-10">
                            <CheckCircle size={80} className="text-green-600" />
                         </div>
                         
                         <div className="flex items-center gap-2 mb-4">
                            <div className="p-1 bg-green-100 rounded-full">
                               <CheckCircle size={20} className="text-green-600 fill-current bg-white rounded-full" />
                            </div>
                            <span className="text-green-800 font-bold text-sm uppercase tracking-wide">Verified Authentic</span>
                         </div>

                         <div className="space-y-3 relative z-10">
                            <div>
                               <p className="text-xs text-green-700 font-medium uppercase">Certification</p>
                               <h3 className="text-lg font-bold text-green-900 leading-tight">{result.examTitle}</h3>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 pt-2">
                               <div>
                                  <p className="text-xs text-green-700 font-medium uppercase mb-1 flex items-center gap-1"><User size={12}/> Candidate</p>
                                  <p className="text-green-900 font-semibold text-sm">{result.candidateName}</p>
                               </div>
                               <div>
                                  <p className="text-xs text-green-700 font-medium uppercase mb-1 flex items-center gap-1"><Hash size={12}/> ID</p>
                                  <p className="text-green-900 font-semibold text-sm font-mono">{result.certificateId}</p>
                               </div>
                               <div>
                                  <p className="text-xs text-green-700 font-medium uppercase mb-1 flex items-center gap-1"><Calendar size={12}/> Issued</p>
                                  <p className="text-green-900 font-semibold text-sm">
                                     {result.issuedAt ? new Date(result.issuedAt).toLocaleDateString() : 'N/A'}
                                  </p>
                               </div>
                               {result.score != null && (
                                 <div>
                                    <p className="text-xs text-green-700 font-medium uppercase mb-1 flex items-center gap-1"><Award size={12}/> Score</p>
                                    <p className="text-green-900 font-semibold text-sm">{Math.round(result.score)}%</p>
                                 </div>
                               )}
                            </div>
                         </div>
                      </div>
                   )}

                   {/* NOT FOUND / ERROR STATE */}
                   {(status === "not_found" || status === "error") && (
                      <div className="bg-red-50 border border-red-100 rounded-2xl p-6 flex items-start gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                         <div className="p-2 bg-red-100 rounded-full shrink-0">
                            <XCircle size={24} className="text-red-600" />
                         </div>
                         <div>
                            <h3 className="text-red-900 font-bold mb-1">Verification Failed</h3>
                            <p className="text-red-700 text-sm leading-relaxed">
                               {message || "We could not find a certificate matching those details. Please check the ID and try again."}
                            </p>
                         </div>
                      </div>
                   )}
                   
                </div>
             </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="py-6 text-center text-gray-400 text-xs">
         &copy; {new Date().getFullYear()} Orivon Academy. All records are secured and encrypted.
      </footer>

    </div>
  );
}

export default VerifyCertificate;